import { Button } from '@payaca/ambrosia';
import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';

import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import ValidationFeedbackBlock from '@payaca/components/validationFeedbackBlock/ValidationFeedbackBlock';
import { getArrayMustNotBeEmptyFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import { requestConfirmPurchaseOrder } from '@payaca/store/materialsList/materialsListActions';
import { ConfirmPurchaseOrderRequestData } from '@payaca/store/materialsList/materialsListTypes';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { HydratedPurchaseOrder } from '@payaca/types/materialsListTypes';
import {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import './ConfirmPurchaseOrderControl.sass';
import PurchaseOrderMaterialPurchaseRecordsControl from './PurchaseOrderMaterialPurchaseRecordsControl';

const fieldValidators = {
  materialPurchaseRecords: [
    getArrayMustNotBeEmptyFieldValidator({
      customErrorMessage: 'You must mark at least one material as purchased',
    }),
  ],
};

type Props = {
  purchaseOrder: HydratedPurchaseOrder;
  onConfirmPurchaseOrderSuccess?: () => void;
};
const ConfirmPurchaseOrderControl: FC<Props> = ({
  purchaseOrder,
  onConfirmPurchaseOrderSuccess,
}: PropsWithChildren<Props>): JSX.Element => {
  const dispatch = useDispatch();
  const [showValidationFeedback, setShowValidationFeedback] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  const materialPurchaseIntents = purchaseOrder.materialPurchaseIntents;

  const initialFormState = useMemo(() => {
    return {
      id: purchaseOrder.id,
      materialPurchaseRecords: materialPurchaseIntents.map((mpi) => {
        return {
          materialsListMaterialId: mpi.materialsListMaterialId,
          materialQuantity: mpi.materialQuantity,
          price: mpi.predictedUnitPriceExcludingTax,
          taxRateId: mpi.predictedTaxRateId,
        };
      }),
    };
    // Do not need any other dependencies here; materialPurchaseIntents and supplierMaterials will not change, and including them as deps causes unwanted re-renders
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'materialPurchaseIntents'
  }, [purchaseOrder.id]);

  const onSubmit = useCallback(
    (formState: ConfirmPurchaseOrderRequestData) => {
      setIsProcessing(true);
      dispatch(
        requestConfirmPurchaseOrder(
          formState,
          () => {
            setIsProcessing(false);
            onConfirmPurchaseOrderSuccess?.();
          },
          () => {
            setIsProcessing(false);
            setSubmissionError(
              'Something went wrong confirming this purchase order.'
            );
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [onConfirmPurchaseOrderSuccess]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <div className="form-body">
            <p>
              Select all materials which have been purchased from the supplier
            </p>
            <PurchaseOrderMaterialPurchaseRecordsControl
              materialPurchaseRecords={formState.materialPurchaseRecords}
              materialPurchaseIntents={purchaseOrder.materialPurchaseIntents}
              onChange={(materialPurchaseRecords) => {
                onFieldChange({
                  materialPurchaseRecords: materialPurchaseRecords,
                });
              }}
            />
            {showValidationFeedback && (
              <ValidationFeedbackBlock
                validationResults={[validationState.materialPurchaseRecords]}
                isDismissable={true}
                onDismissed={() => setShowValidationFeedback(false)}
              />
            )}
            {submissionError && (
              <FeedbackBlock
                isDismissable={true}
                onDismissed={() => setSubmissionError('')}
                feedbackLevel={FeedbackLevel.ERROR}
              >
                <span>{submissionError}</span>
              </FeedbackBlock>
            )}
          </div>
          <div className="buttons-container">
            <Button
              isProcessing={isProcessing}
              onClick={() =>
                isValid
                  ? !isProcessing && onSubmit(formState)
                  : setShowValidationFeedback(true)
              }
            >
              Confirm
            </Button>
          </div>
        </>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'purchaseOrder.materialPurchaseIntents'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: purchaseOrder.id
    [
      purchaseOrder.id,
      showValidationFeedback,
      isProcessing,
      onSubmit,
      submissionError,
    ]
  );

  return (
    <div className="confirm-purchase-order-control">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
      />
    </div>
  );
};

export default ConfirmPurchaseOrderControl;
